import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew, sheetTextGeometryNew } from "building-types/views/sheetGeometryTypes";
import { numDiv, sheetInchesNew, sheetPointsNew, sheetPointsToInches } from "common/utils/nominalNumbers";
import { PlacementXAnchor, PlacementYAnchor, TextAlignment, TextVerticalAlignment } from "common/utils/textAlignment";
import type { TransformWithComponents } from "doc/plans/sheetGeometryTools";
import { sheetGeometryTransform } from "doc/plans/sheetGeometryTools";
import { mat3Scale, mat3Translate } from "geom/mat3";
import { polyLine2Transform } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";

interface ElevSlopeTagProps {
  x: number;
  y: number;
  slope: number[];
  flipHorizontal: boolean;
}

const elevSlopeTagDefault: ElevSlopeTagProps = {
  x: 0,
  y: 0,
  slope: [4, 12],
  flipHorizontal: false,
};

interface WeightedSheetGeometry {
  readonly geometry: SheetGeometry;
  readonly weight: number;
}

export function generateElevSlopeTagSheetGeometry(props: Partial<ElevSlopeTagProps>): WeightedSheetGeometry[] {
  const resolvedProps = { ...elevSlopeTagDefault, ...props };
  const [rise, run] = resolvedProps.slope;
  const flip = resolvedProps.flipHorizontal ? -1 : 1;
  const mRise = sheetInchesNew(rise * 0.025);
  const mRun = sheetInchesNew(run * 0.025);
  const riseAdjust = numDiv(mRise, 5);

  // Create triangle SheetGeometry
  const p0 = [-mRun / 2, 0];
  const p1 = [p0[0], p0[1] - mRise];
  const p2 = [p1[0] + mRun, p1[1]];
  const triPolyLine = polyLine2New(p0.concat(p1.concat(p2.concat(p0))));
  polyLine2Transform(triPolyLine, mat3Scale(flip, 1));

  const triSheetGeometry = sheetPolylineGeometryNew({
    paths: [triPolyLine],
  });

  // Labels are deliberately unstyled: the ROOF_SLOPE_SYMBOL stylesheet owns font/size/color and
  // the global text style owns the outline, so View Template overrides apply.
  const runSheetGeometry = sheetTextGeometryNew({
    x: 0,
    y: -(mRise + sheetPointsToInches(sheetPointsNew(4))),
    text: `${run}`,
    rotation: 0,
    alignment: TextAlignment.LEFT,
    verticalAlignment: TextVerticalAlignment.BOTTOM,
    placementYAnchor: PlacementYAnchor.BOTTOM,
    placementXAnchor: PlacementXAnchor.CENTER,
  });

  const placementXAnchor = resolvedProps.flipHorizontal ? PlacementXAnchor.LEFT : PlacementXAnchor.RIGHT;
  const riseSheetGeometry = sheetTextGeometryNew({
    x: ((mRun + sheetPointsToInches(sheetPointsNew(11))) * -flip) / 2,
    y: -mRise / 2,
    text: `${rise}`,
    rotation: 0,
    alignment: TextAlignment.LEFT,
    verticalAlignment: TextVerticalAlignment.CENTER,
    placementYAnchor: PlacementYAnchor.CENTER,
    placementXAnchor,
  });

  // Scale and move all generated geometries to roof
  const transformA = mat3Translate(resolvedProps.x, resolvedProps.y + riseAdjust);

  const sheetGeometries: WeightedSheetGeometry[] = [];
  const transform: TransformWithComponents = {
    transform: transformA,
    transformRotation: 0,
    transformScale: [1, 1],
  };

  sheetGeometries.push({
    geometry: sheetGeometryTransform(triSheetGeometry, transform),
    weight: 3,
  });
  sheetGeometries.push({
    geometry: sheetGeometryTransform(riseSheetGeometry, transform),
    weight: 6,
  });
  sheetGeometries.push({
    geometry: sheetGeometryTransform(runSheetGeometry, transform),
    weight: 6,
  });

  return sheetGeometries;
}
